import { z } from 'zod';
import { createSceneSchema, placeSceneSchema, updateLessonSchema, updateQuestionSchema, updateSceneSchema, } from './authoring.js';
import { castIdSchema } from './cast.js';
import { idSchema } from './common.js';
import { moduleCastSchema, plannedLessonFilterSchema, plannedLessonIdSchema, plannedLessonSchema, storyBeatSchema, storyRevisionSchema, storySchema, updatePlannedLessonSchema, } from './curriculum.js';
import { castAssignmentFieldsSchema, castAssignmentSchema, lessonCastIdsSchema, storyFiguresSchema, } from './generation.js';
import { copyEntrySchema, flowStepSchema } from './onboarding.js';
/**
 * A conversation with the planner — GEN-52.
 *
 * Separate from `generation.ts`, which describes a *run*: a job, its rows, and what the
 * reviewer found. Nothing here is stored — see
 * `docs/superpowers/specs/2026-08-31-planner-chat-design.md`.
 *
 * Deliberately not in the package barrel — import `@platform/schema/chat`. Same reason as
 * `content` and `generation`: the barrel is on the mobile boot path.
 */
/**
 * Who said a turn.
 *
 * `operator` rather than `user`, because in this app a user is a learner and this
 * conversation has nothing to do with one.
 */
export const chatRoleSchema = z.enum(['operator', 'planner']);
export const chatTurnSchema = z.strictObject({
    role: chatRoleSchema,
    text: z.string().min(1).max(4000),
});
/**
 * How many turns one conversation carries.
 *
 * The transcript is re-sent whole on every exchange, so this bounds the request as well
 * as the prompt. Twenty is roughly a session's worth of back-and-forth on one lesson;
 * past that the operator has a different question and is better served by a fresh panel
 * than by a model re-reading forty turns to answer it.
 */
export const MAX_TURNS = 20;
/**
 * How much of a collection's sequence one exchange carries — PATH-01.
 *
 * Beside `MAX_TURNS` because it is the same kind of bound, and here rather than in the
 * generator because two packages need the one number: the API reads this many rows, and
 * the prompt builder renders them. Split, the reader would fetch a different count than
 * the writer showed and nothing would say so.
 *
 * Nothing caps a collection in the schema — an operator may put two hundred lessons in
 * one path — and fifty is more sequence than an argument about ordering needs.
 */
export const CONTEXT_LESSONS = 50;
/**
 * How many lessons one conversation may hold in view — GEN-70.
 *
 * Eight, because the prompt carries every scene of every one of them and the operator is
 * asking one question about the set. Far below `CONTEXT_LESSONS`, and deliberately: a
 * collection is offered as a *sequence* — titles in order — where these are offered whole.
 */
export const MAX_LESSONS_IN_VIEW = 8;
export const askPlannerSchema = z
    .strictObject({
    turns: z.array(chatTurnSchema).min(1).max(MAX_TURNS),
    lessonIds: z
        .array(idSchema)
        .min(1)
        .max(MAX_LESSONS_IN_VIEW)
        .optional()
        .describe('The lessons to offer as context. Absent is how the operator declines it — there is no flag to honour, only a field that was not sent.'),
    collectionId: idSchema
        .optional()
        .describe('The path, course or module to offer instead — PATH-01. Declined the same way, by not being sent.'),
    scene: z
        .strictObject({ lessonId: idSchema, sceneId: idSchema })
        .optional()
        .describe('The scene the operator has open, which is what the question is about. Sent whether or not the lesson is offered: the lesson is background, this is the subject.'),
    onboarding: z
        .strictObject({ stepId: z.string().min(1).max(40) })
        .optional()
        .describe('The onboarding screen open in the console — ONB-21. The server reads the draft flow, or the live one without a draft. Declined by not being sent.'),
})
    .check((ctx) => {
    if (ctx.value.scene === undefined || ctx.value.collectionId === undefined)
        return;
    ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['scene'],
        message: 'A scene belongs to a lesson, not to a collection.',
    });
})
    .check((ctx) => {
    // Two contexts is not a richer question, it is a question about which one the server
    // prefers — and a precedence rule is the kind nothing in the console exercises and
    // nobody remembers. Refused here so neither the service nor the prompt builder has to
    // have an opinion. Several *lessons* are one context, which is why they are one field.
    if (ctx.value.lessonIds === undefined ||
        ctx.value.collectionId === undefined)
        return;
    ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['collectionId'],
        message: 'Offer lessons or a collection, not both. The operator is looking at one thing.',
    });
})
    .check((ctx) => {
    const { onboarding, lessonIds, collectionId, scene } = ctx.value;
    if (onboarding === undefined ||
        (lessonIds === undefined &&
            collectionId === undefined &&
            scene === undefined))
        return;
    ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['onboarding'],
        message: 'An onboarding screen is asked about on its own, without lessons or a collection.',
    });
})
    .describe('POST /authoring/chat');
/**
 * An edit the planner suggests, in the shape the authoring endpoints take — GEN-66.
 *
 * Picked from the `update*` schemas rather than restated, so a proposal is by construction
 * a body an existing `PATCH` accepts. `lessonId` is on every arm because it says which
 * lesson the diff is read against, not which row to find.
 *
 * `arc` is GEN-77's, and the one that is not a `PATCH` body: which scenes a lesson has is
 * not a column, so it is composed out of two calls instead.
 *
 * Every line of it: `docs/decisions/planner-proposals.md`.
 */
const changesSomething = { message: 'A proposal must change a field.' };
/**
 * A scene's editable fields — every one the console can put back, and no more — GEN-73.
 *
 * Picked rather than subtracted, which inverts the default that matters: the next column
 * added to the PATCH would otherwise become proposable with no `inverseScene` arm to undo
 * it and no `answerFields` entry to diff it. `unit` is out because `answerFields` does not
 * carry it; `sourceId` because the model is shown no source ids.
 *
 * `keys` is the whole `rekeySceneItemSchema` since GEN-77, where GEN-73 took `correctAmount`
 * out of it alone — the review payload carries the other two per item, so the way back was
 * readable all along and only the reader was missing.
 */
const proposedSceneSchema = updateSceneSchema.pick({
    prompt: true,
    body: true,
    items: true,
    keys: true,
    min: true,
    max: true,
    step: true,
    targetMin: true,
    targetMax: true,
    poolTotal: true,
});
/** Where a scene may sit — the bound `placeSceneSchema` keeps, and not a second copy. */
const scenePositionSchema = placeSceneSchema.shape.position;
/**
 * A whole scene the planner wrote — GEN-77.
 *
 * `createSceneSchema` rather than a kind and a set of placeholders, because a mechanic the
 * operator asked for arrives finished: the options, the buckets and the band come with it,
 * and the seeds in `order.ts` are what the *hand* path falls back to when nobody wrote
 * them. Three columns are dropped: `questionId` and `sourceId` name rows the model is
 * never shown, and `imageUrl` is a URL it could only invent.
 */
const proposedSceneSeedSchema = createSceneSchema.omit({
    questionId: true,
    sourceId: true,
    imageUrl: true,
});
/**
 * A change to which scenes a lesson has, and in what order — GEN-77.
 *
 * Four operations rather than a new scene list, because a list is a diff nobody can read:
 * the operator is shown *one* change and applies it. Each is composed by the console out
 * of `POST /authoring/scenes` and `PUT /authoring/lessons/:id/scenes` — the same two calls
 * the Add, Remove and drag controls make, so an applied proposal is the hand path.
 *
 * `retype` is one operation and not a remove beside an add, because the two halves are not
 * separately applicable: between them the lesson is a scene short of `SCENES_MIN`, which
 * `removable` refuses and the publish gate reports.
 */
const arcChangeSchema = z.discriminatedUnion('op', [
    z.strictObject({
        op: z.literal('add'),
        scene: proposedSceneSeedSchema,
        position: scenePositionSchema
            .optional()
            .describe('Where it goes. Absent puts it last.'),
    }),
    z.strictObject({ op: z.literal('remove'), sceneId: idSchema }),
    z.strictObject({
        op: z.literal('move'),
        sceneId: idSchema,
        position: scenePositionSchema,
    }),
    z.strictObject({
        op: z.literal('retype'),
        sceneId: idSchema,
        scene: proposedSceneSeedSchema.describe('What it becomes, in its place. The scene it replaces is dropped.'),
    }),
]);
/** An added screen's own words, picked from the QUESTION arm, which has every one. */
const onboardingStepTextSchema = flowStepSchema.options[2]
    .pick({ name: true, title: true, body: true, button: true, skipLabel: true })
    .partial();
export const proposedEditSchema = z
    .discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('lesson'),
        lessonId: idSchema,
        body: updateLessonSchema
            .pick({ title: true, summary: true, personas: true, tags: true })
            .refine((body) => Object.keys(body).length > 0, changesSomething),
    }),
    z.strictObject({
        kind: z.literal('scene'),
        lessonId: idSchema,
        sceneId: idSchema,
        body: proposedSceneSchema.refine((body) => Object.keys(body).length > 0, changesSomething),
    }),
    z.strictObject({
        kind: z.literal('question'),
        lessonId: idSchema,
        questionId: idSchema,
        body: updateQuestionSchema.pick({ explanation: true }).required(),
    }),
    z.strictObject({
        kind: z.literal('arc'),
        lessonId: idSchema,
        change: arcChangeSchema,
    }),
    // ONB-21: applied into the onboarding page's unsaved draft, never written here.
    z.strictObject({
        kind: z.literal('onboardingCopy'),
        ...copyEntrySchema.shape,
    }),
    z.strictObject({
        kind: z.literal('onboardingStep'),
        stepId: z.string().min(1).max(40),
        body: onboardingStepTextSchema.refine((body) => Object.keys(body).length > 0, changesSomething),
    }),
])
    .describe('One edit the planner suggests, which nobody has applied.');
export const isOnboardingProposal = (edit) => edit.kind === 'onboardingCopy' || edit.kind === 'onboardingStep';
/**
 * The entries that are proposals, out of whatever the model or the SDK handed over.
 *
 * One helper rather than two loops: the generator reads a fenced JSON block and the
 * console reads a response whose `const` discriminator the SDK renders as plain `string`.
 * "A malformed entry is dropped, the rest are kept" is a rule the two sides have to agree
 * on — a sixth the model got wrong says nothing about the other five.
 */
const wellFormed = (schema) => (written) => written.flatMap((one) => {
    const parsed = schema.safeParse(one);
    return parsed.success ? [parsed.data] : [];
});
export const proposedEdits = wellFormed(proposedEditSchema);
/** What one exchange cost: the SDK's dollar estimate, and the tokens actually spent. */
const answered = {
    text: z.string().min(1),
    costUsd: z.number().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
};
export const plannerAnswerSchema = z
    .strictObject({
    ...answered,
    proposals: z
        .array(proposedEditSchema)
        .max(MAX_LESSONS_IN_VIEW * 2)
        .describe('The concrete edits the planner suggested, empty when it suggested none. Nothing is written for them — the operator applies each, or does not.'),
})
    .describe('One answer, and what asking for it cost.');
/**
 * Asking about the curriculum plan rather than built lessons — CURR-24. The plan is its
 * own domain, so this is its own request and answer: a planning proposal is not a body any
 * `/authoring` endpoint takes, and the console's lesson proposals do not know it.
 */
/** The most Lesson IDs one selection sends; the whole plan is the `curriculum` scope. */
export const MAX_SELECTED = 1_000;
/**
 * A pattern matching any of `ids` as a whole Lesson ID, longest first so `BUS-01-01` does
 * not stop short inside `BUS-01-010`. The API cites by it and the console links by it, so
 * they agree on what an answer names.
 */
export function wholeLessonIds(ids) {
    const body = [...ids]
        .sort((a, b) => b.length - a.length)
        .map((id) => id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
    return `(?<![\\w-])(${body})(?![\\w-])`;
}
export const planningScopeSchema = z
    .discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('lesson'),
        lessonId: plannedLessonIdSchema,
    }),
    z.strictObject({
        kind: z.literal('selected'),
        lessonIds: z.array(plannedLessonIdSchema).min(1).max(MAX_SELECTED),
    }),
    z.strictObject({
        kind: z.literal('filter'),
        filter: plannedLessonFilterSchema,
    }),
    z.strictObject({ kind: z.literal('curriculum') }),
])
    .describe('Which planned lessons the question is about: one, a selection, the list endpoint’s filter, or all of them.');
export const askCurriculumSchema = z
    .strictObject({
    turns: z.array(chatTurnSchema).min(1).max(MAX_TURNS),
    scope: planningScopeSchema,
    briefPart: z
        .number()
        .int()
        .min(1)
        .default(1)
        .describe('Which part of the scope’s writer briefs and sources to read, when they do not fit in one: `coverage.briefParts` says how many there are.'),
})
    .describe('POST /curriculum/chat');
/** The planning fields a proposal may change — the lesson's own text, never its place. */
const plannedTextSchema = updatePlannedLessonSchema.pick({
    name: true,
    description: true,
    writerBrief: true,
    primarySourceName: true,
    primarySourceUrl: true,
    additionalSourceName: true,
    additionalSourceUrl: true,
});
/** What the model writes: which lesson, and the fields it would set. */
export const proposedPlanningEditSchema = z.strictObject({
    lessonId: plannedLessonIdSchema,
    body: plannedTextSchema.refine((body) => Object.keys(body).length > 0, changesSomething),
});
export const proposedPlanningEdits = wellFormed(proposedPlanningEditSchema);
/**
 * What the model writes for the one module in view — CURR-41: the whole story, and a beat
 * for each of its lessons. `kind` tells it apart from a lesson edit in the same block.
 */
export const proposedStoryEditSchema = z.strictObject({
    kind: z.literal('story'),
    story: storySchema,
    figures: storyFiguresSchema
        .optional()
        .describe('Absent keeps the saved figures.'),
    beats: z
        .array(z.strictObject({
        lessonId: plannedLessonIdSchema,
        beat: storyBeatSchema,
    }))
        .min(1),
});
export const proposedStoryEdits = wellFormed(proposedStoryEditSchema);
export const planningStoryProposalSchema = z
    .strictObject({
    moduleId: z.string(),
    module: z.string(),
    storyRevision: storyRevisionSchema.describe('The revision it was proposed from. Accepting it once the story has been saved again is refused with a 409.'),
    before: z
        .string()
        .describe('The saved story when the question was asked; empty for none.'),
    after: storySchema,
    figures: z
        .strictObject({ before: storyFiguresSchema, after: storyFiguresSchema })
        .describe('The case figures saved with the story — GEN-151.'),
    beats: z
        .array(z.strictObject({
        lessonId: plannedLessonIdSchema,
        name: z.string(),
        before: z.string(),
        after: storyBeatSchema.min(1),
    }))
        .min(1)
        .describe('One per planned lesson in the module, in its order when proposed. Accepting it once that order or membership has changed is refused with a 409.'),
    compared: z
        .strictObject({
        inCourse: z.number().int().min(0),
        elsewhere: z.number().int().min(0),
    })
        .describe('How many other saved module stories the planner was given to keep this one distinct: the course’s, and the most recently saved elsewhere. Never the whole catalog.'),
})
    .describe('A module story and its beats, which nobody has accepted.');
/**
 * What the model writes to cast the one module in view — CURR-44: up to three of the cast,
 * and the characters each lesson uses. The server gives each its id, as the admin does.
 */
export const proposedCastEditSchema = z.strictObject({
    kind: z.literal('cast'),
    cast: z
        .array(castAssignmentFieldsSchema
        .omit({ id: true, name: true })
        .extend({ character: castIdSchema }))
        .min(1),
    lessons: z
        .array(z.strictObject({
        lessonId: plannedLessonIdSchema,
        cast: z.array(castIdSchema),
    }))
        .min(1),
});
export const proposedCastEdits = wellFormed(proposedCastEditSchema);
export const planningCastProposalSchema = z
    .strictObject({
    moduleId: z.string(),
    module: z.string(),
    storyRevision: storyRevisionSchema.describe('The revision it was proposed from. Accepting it through PATCH /curriculum/modules/:id once the story or cast has been saved again is refused with a 409.'),
    before: z
        .array(castAssignmentSchema)
        .describe('The saved cast when the question was asked; empty for none.'),
    after: moduleCastSchema,
    lessons: z
        .array(z.strictObject({
        lessonId: plannedLessonIdSchema,
        name: z.string(),
        before: lessonCastIdsSchema,
        after: lessonCastIdsSchema,
    }))
        .min(1)
        .describe('One per planned lesson in the module, in its order when proposed.'),
})
    .describe("A module's cast and each lesson's subset, which nobody has accepted.");
export const planningProposalSchema = z
    .strictObject({
    lessonId: plannedLessonIdSchema,
    before: plannedTextSchema.describe('Each changed field as the plan held it when the question was asked.'),
    after: plannedTextSchema.describe('Each changed field as proposed.'),
    built: z
        .boolean()
        .describe('The lesson has been built. Applying changes the plan only; the built lesson stays as it is until it is rebuilt.'),
})
    .describe('One planning edit, which nobody has applied.');
const scopeKindSchema = z.enum(['lesson', 'selected', 'filter', 'curriculum']);
export const curriculumAnswerSchema = z
    .strictObject({
    ...answered,
    examined: z
        .literal('planning')
        .describe('The plan’s rows. No built lesson’s content is read.'),
    scope: z.strictObject({
        kind: scopeKindSchema,
        label: z.string().describe('The scope, in words.'),
    }),
    coverage: z
        .strictObject({
        inScope: z
            .number()
            .int()
            .min(0)
            .describe('Planned lessons the scope matches.'),
        read: z
            .number()
            .int()
            .min(0)
            .describe('Of those, how many were read: ID, place, name, status and description.'),
        briefsRead: z
            .number()
            .int()
            .min(0)
            .describe('Of those, how many writer briefs and sources were read.'),
        briefPart: z.number().int().min(1),
        briefParts: z.number().int().min(1),
        notFound: z
            .array(plannedLessonIdSchema)
            .describe('Lesson IDs asked for that the plan does not have.'),
    })
        .describe('How much of the scope the answer rests on.'),
    lessonIds: z
        .array(plannedLessonIdSchema)
        .describe('The read Lesson IDs the answer relies on, in curriculum order: named, inside a range it names, proposed for, or the scope’s own lesson.'),
    proposals: z.array(planningProposalSchema),
    storyProposals: z
        .array(planningStoryProposalSchema)
        .max(1)
        .describe('A story for the module in scope, offered only when every lesson in scope is in one module.'),
    castProposals: z
        .array(planningCastProposalSchema)
        .max(1)
        .describe('A cast for the module in scope, offered only when every lesson in scope is in one module.'),
})
    .describe('One answer about the plan, and what asking for it cost.');
/** The lessons a scope summary names; past this it says how many more there are. */
export const SCOPE_LESSONS_LISTED = 100;
export const describePlanningScopeSchema = z
    .strictObject({ scope: planningScopeSchema })
    .describe('POST /curriculum/chat/scope');
export const planningScopeSummarySchema = z
    .strictObject({
    label: z.string().describe('The scope, in the words an answer gives it.'),
    lessonCount: z.number().int().min(0),
    courses: z
        .array(z.strictObject({ name: z.string(), modules: z.array(z.string()) }))
        .describe('Each course in scope with its modules in scope, in curriculum order.'),
    lessons: z
        .array(plannedLessonSchema.pick({ lessonId: true, name: true }))
        .max(SCOPE_LESSONS_LISTED)
        .describe('The first lessons in scope, in curriculum order.'),
})
    .describe('What a question in this scope would be about, before it is asked.');
export const applyPlanningProposalsSchema = z
    .strictObject({
    proposals: z
        .array(planningProposalSchema)
        .describe('As the chat returned them; `built` is not read.')
        .min(1)
        .max(MAX_SELECTED),
})
    .describe('POST /curriculum/proposals/apply');
export const appliedProposalsSchema = z
    .strictObject({
    applied: z.array(plannedLessonSchema),
    refused: z.array(z.strictObject({
        lessonId: plannedLessonIdSchema,
        reason: z.enum(['missing', 'stale']),
        message: z.string(),
    })),
})
    .describe('Each proposal is applied or refused on its own: one that cannot land leaves the rest.');
export const applyStoryProposalSchema = planningStoryProposalSchema.describe('POST /curriculum/proposals/apply-story, as the chat returned it; `before`, `name` and `compared` are not read.');
//# sourceMappingURL=chat.js.map